import React from "react";
import _ from "lodash";

import InfoIcon from "./alertIcons/InfoIcon";
import SuccessIcon from "./alertIcons/SuccessIcon";
import ErrorIcon from "./alertIcons/ErrorIcon";
import WarningIcon from "./alertIcons/WarningIcon";
import CloseIcon from "./alertIcons/CloseIcon";

const alertStyle = {
  backgroundColor: "#151515",
  color: "white",
  padding: "10px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  boxShadow: "0px 2px 2px 2px rgba(0, 0, 0, 0.03)",
  fontFamily: "Arial",
  boxSizing: "border-box",
  zIndex: 3,
  position: "relative",
};

const buttonStyle = {
  marginLeft: "20px",
  border: "none",
  backgroundColor: "transparent",
  cursor: "pointer",
  color: "#FFFFFF",
};

export const AlertTemplate = ({ message, options, style, close }) => {
  return (
    <div style={{ ...alertStyle, ...style }}>
      {options.type === "info" && <InfoIcon />}
      {options.type === "success" && <SuccessIcon />}
      {options.type === "warning" && <WarningIcon />}
      {options.type === "error" && <ErrorIcon />}
      <span style={{ flex: 2 }}>{message}</span>
      <button onClick={close} style={buttonStyle}>
        <CloseIcon />
      </button>
    </div>
  );
};

export function showErrors(error, alertRef) {
  if (error.response.status === 400) {
    for (let key in error.response.data) {
      let keyName;
      if (key !== "non_field_errors") {
        keyName = key.replace("_", " ");
        keyName = keyName.charAt(0).toUpperCase() + keyName.slice(1);
        keyName += ": ";
      } else {
        keyName = "";
      }
      for (let i = error.response.data[key].length - 1; i >= 0; i--) {
        alertRef.current.sendAlert(keyName + error.response.data[key][i]);
      }
    }
  } else if (error.response.status === 403) {
    for (let key in error.response.data) {
      let keyName = key.replace("_", " ");
      keyName = keyName.charAt(0).toUpperCase() + keyName.slice(1);
      keyName += ": ";
      for (let i = error.response.data[key].length - 1; i >= 0; i--) {
        alertRef.current.sendAlert(keyName + error.response.data[key][i]);
      }
    }
  } else {
    alertRef.current.sendAlert("Something went wrong");
  }
}

export const orderStatusOptions = [
  {
    id: 0,
    badgeClass: "grey",
    name: "In progress",
  },
  {
    id: 1,
    badgeClass: "orange",
    name: "Preparing",
  },
  {
    id: 2,
    badgeClass: "yellow",
    name: "Shipped",
  },
  {
    id: 3,
    badgeClass: "green",
    name: "Received",
  },
];

export const getItemPresentation = (item) => {
  if (item.packs && item.master_packing_number) {
    const packAmount = item.master_packing_number / item.packs;
    return `${item.packs} pack${item.packs > 1 ? "s" : ""} of ${packAmount}`;
  } else {
    return null;
  }
};

export const getItemMeatPresentation = (item, presentation) => {
  switch (presentation) {
    case "case":
      return "Case";
    case "piece":
      return "Piece";
    case "weight":
      return item.measure_unit;
    default:
      return item.measure_unit;
  }
};

// ---------------------------------------------------------------------------
// Market order figures (order_stats sent by the backend for each item)
// ---------------------------------------------------------------------------
export const formatQty = (value) => {
  if (value === null || value === undefined) return "-";
  return Number.isInteger(value) ? `${value}` : `${Math.round(value * 10) / 10}`;
};

export const formatOrderPresentation = (presentation, measureUnit) => {
  switch (presentation) {
    case "piece":
      return "pc";
    case "weight":
      return measureUnit || "";
    default:
      return "";
  }
};

export const formatPackSize = (item, stats) => {
  if (stats && stats.pack_size) return stats.pack_size;
  return getItemPresentation(item) || "-";
};

export const formatCaseWeight = (item, stats) => {
  if (!stats || !stats.is_butcher || !stats.avg_case_weight) return "-";
  return `≈ ${formatQty(stats.avg_case_weight)} ${item.measure_unit || "lb"}`;
};

// Quantity of the item in the store's previous sent order
export const getLastOrderQty = (orderItem) => {
  const stats = orderItem.order_stats;
  const qty = stats ? stats.last_order_qty : orderItem.previous_ordered;
  return qty || qty === 0 ? formatQty(qty) : "-";
};

export const formatMoney = (value) => {
  if (value === null || value === undefined) return "-";
  return `$${Number(value).toFixed(2)}`;
};

export const groupItemsByStation = (items) => {
  const station = items && items[0] && typeof items[0].station === "string" ? "station" : "station.name";
  const orderedByItemStationAndName = _.orderBy(items, [station, "name"], ["asc", "asc"]);
  return _.groupBy(orderedByItemStationAndName, (item) => {
    if (typeof item.station === "string") {
      return item.station;
    } else {
      return item.station.name;
    }
  });
};

export const groupItemsByStationWithoutZeroQty = (items) => {
  const station = items && items[0] && typeof items[0].station === "string" ? "station" : "station.name";
  const itemsWithoutZeroQty = _.filter(items, (item) => item.amount > 0);
  const orderedByItemStationAndName = _.orderBy(itemsWithoutZeroQty, [station, "name"], ["asc", "asc"]);
  return _.groupBy(orderedByItemStationAndName, (item) => {
    if (typeof item.station === "string") {
      return item.station;
    } else {
      return item.station.name;
    }
  });
};

export const groupInventoryByItem = (inventories) => {
  return _.groupBy(inventories, (inv) => {
    return inv.item.name;
  });
};

export const purchaseOrderStatusOptions = [
  {
    id: 0,
    badgeClass: "grey",
    name: "In progress",
  },
  {
    id: 1,
    badgeClass: "orange",
    name: "Awaiting purchase",
  },
  {
    id: 2,
    badgeClass: "green",
    name: "Finished",
  },
];

export const marketPurchaseOrderStatusOptions = [
  {
    id: 0,
    badgeClass: "grey",
    name: "In progress",
  },
  {
    id: 2,
    badgeClass: "green",
    name: "Finished",
  },
];

export const purchaseOrderTransportConditions = [
  {
    id: "good",
    name: "Good",
  },
  {
    id: "bad",
    name: "Bad",
  },
];

export const renderResponseAsString = (response) => {
  if (typeof response === 'object') {
    return JSON.stringify(response);
  }
  return response
};